import React, {
  Dispatch,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { ApiTransaction } from '@vend/types'
import {
  TerminalEventMonitoring,
  TerminalModel,
} from '@lightspeed-payments/payments-terminal-sdk'
import {
  wait,
  trackUserEvent,
  removeFromStorage,
  saveToStorage,
} from '@vend/utilities'
import {
  SwiperError,
  LSPAYServerDrivenModalOptions,
  convertDeviceTypeValueToKey,
  TerminalInfo,
} from '../../../helpers/lspay.types'
import { ErrorContent } from '../../modal-states/ErrorContent'
import { useVdFeatureManager } from '../../../../../../ng-compat/hooks'
import {
  isTransactionCompleted,
  reprocessTransaction,
} from '../../../helpers/lspayTransaction'
import {
  LSPAY_ERROR,
  LSPAY_TRANSACTION_STATUS,
  SANDBOX_FLAG,
  SWIPER_CANCEL_REASON,
  TRANSACTION_TYPE,
} from '../../../helpers/lspayConstants'
import {
  getPaymentDeclinedError,
  isCardSpecificDecline,
} from '../../../helpers/errorChecking'
import { getMonitoringInstance } from '../../../helpers/terminalMonitoring'
import { WEB_REGISTER } from '../../../../../web-register.constant'
import { TAG, trackLSPayServerError } from '../../../hooks/errorTracking'
import {
  ACTIVE_LSPAY_TRANSACTION,
  ActiveLSPayTransaction,
} from '../../../../../../constants/payments'
import { sendTerminalEvent } from '../../../helpers/terminalMonitoringEventDispatcher'
import { useErrorMessages } from '../../../hooks/useErrorMessages'
import {
  PaymentState,
  PaymentEvent,
} from './hooks/use-lspay-payment-server-reducer/types'
import { useServerPaymentReducer } from './hooks/use-lspay-payment-server-reducer/useServerPaymentReducer'
import { CreatingTransaction } from './components/CreatingTransaction'
import { PollTransaction } from './components/PollTransaction'
import { CaptureTransaction } from './components/CaptureTransaction'
import { RecoverTransaction } from './components/RecoverTransaction'

export const REPROCESS_DELAY = 3000 // 3 seconds

interface LSPAYPaymentServerDrivenProps extends LSPAYServerDrivenModalOptions {
  onError: () => void
  onSuccess: (transaction: Promise<ApiTransaction>) => void
  setDismissible: (dismissible: boolean) => void
  setDisableCNP: (disableCNP: boolean) => void
  setCancelProcessing: (cancelProcessing: () => void) => void
  setSwiperError: (swiperError: SwiperError) => void
  generateTransactionId: () => string
}

const LSPAYPaymentServerDriven: FC<LSPAYPaymentServerDrivenProps> = ({
  amount,
  currency,
  retailerPaymentType,
  outletId,
  register,
  registerSaleId,
  transactionId: registerSalePaymentId,
  paymentIntentId,
  isRecovery,
  withSurcharge,
  tipEligibleAmount,
  onSuccess,
  setDismissible,
  onError,
  setDisableCNP,
  setSwiperError,
  pairing,
  setCancelProcessing,
  generateTransactionId,
}) => {
  // This reducer controls the state changes and side effects
  const [paymentState, dispatch] = useServerPaymentReducer(
    paymentIntentId,
    isRecovery
  )
  const [isRetrying, setIsRetrying] = React.useState(false)
  const getErrorMessage = useErrorMessages()
  const registerId = register.id
  const retailerPaymentTypeId = retailerPaymentType.id

  const paymentStateRef = useRef(paymentState)
  useEffect(() => {
    paymentStateRef.current = paymentState
  }, [paymentState])

  const isSandbox = useVdFeatureManager().get(SANDBOX_FLAG)
  const terminalMonitoringInstanceRef = useRef<TerminalEventMonitoring | null>(
    null
  )
  if (!terminalMonitoringInstanceRef.current && pairing && !paymentIntentId) {
    terminalMonitoringInstanceRef.current = getMonitoringInstance(
      outletId,
      isSandbox,
      'CLOUD'
    )
  }
  const terminalMonitoringInstance = terminalMonitoringInstanceRef.current
  const deviceType: TerminalModel = convertDeviceTypeValueToKey(
    pairing?.deviceType
  )
  const terminalInfo: TerminalInfo = {
    deviceId: pairing?.deviceID,
    deviceModel: deviceType,
  }

  // When we're done, call the onSuccess callback
  useEffect(() => {
    if (paymentState.status === 'done') {
      onSuccess(Promise.resolve(paymentState.transaction))
    }
  }, [onSuccess, paymentState])

  // If the modal is in an error or cancelled state set the modal to be dismissible
  // otherwise make it non-dismissible
  useEffect(() => {
    if (
      paymentState.status === 'error' ||
      paymentState.status === 'cancelled' ||
      paymentState.status === 'declined'
    ) {
      setDismissible(true)
      return
    }
    setDismissible(false)
  }, [paymentState, setDismissible])

  const handleRetry = async (
    paymentPreviousState: PaymentState,
    dispatch: Dispatch<PaymentEvent>
  ) => {
    // Track the event for retrying the transaction
    trackUserEvent(WEB_REGISTER.DISPLAY_NAME, 'LSD clicked retry button', {
      registerSalePaymentId,
      registerSaleId,
      paymentIntentId:
        'paymentIntentId' in paymentState && paymentState.paymentIntentId,
    })
    if (
      !(
        paymentState.status === 'declined' ||
        paymentState.status === 'cancelled'
      )
    ) {
      if (
        paymentState.status === 'error' &&
        paymentState.previousState.status === 'creatingTransaction'
      ) {
        trackUserEvent(
          WEB_REGISTER.DISPLAY_NAME,
          'LSD Retry creating transaction with new rspId when errored',
          {
            initialRegisterSalePaymentId: registerSalePaymentId,
          }
        )
        generateTransactionId()
      }
      dispatch({
        type: 'RETRY',
        previousState: paymentPreviousState,
      })
      return
    }
    // To handle the cases
    // 1. After a multiple retries, stripe might cancel the payment intent
    // 2. Try to recover a transaction after long time without intent, swiper couldn't recover the transaction
    // In these cases we can't retry the payment intent, we need to create a new one
    if (
      paymentState.status === 'cancelled' &&
      (paymentState.reason === SWIPER_CANCEL_REASON.INTENT_CANCELLED ||
        paymentState.reason === SWIPER_CANCEL_REASON.PAYMENT_INTENT_NOT_FOUND)
    ) {
      const oldRegisterSalePaymentId = paymentState.paymentIntentId

      const newRegisterSalePaymentId = generateTransactionId()

      trackUserEvent(
        WEB_REGISTER.DISPLAY_NAME,
        'LSD user retried a intent cancelled transaction which create a new transaction',
        {
          oldRegisterSalePaymentId,
          newRegisterSalePaymentId,
          registerSaleId,
          reason: paymentState.reason,
        }
      )
      dispatch({
        type: 'CREATE_TRANSACTION',
      })
      return
    }

    // save the active lspay transaction to local storage for recovery during retry
    if (paymentState.status === 'declined') {
      const activeLSPayTransaction: ActiveLSPayTransaction = {
        registerSaleId,
        paymentIntentId: paymentState.paymentIntentId,
        registerSalePaymentId,
        amount: amount.toString(),
      }
      saveToStorage(ACTIVE_LSPAY_TRANSACTION, activeLSPayTransaction)
    }

    try {
      setIsRetrying(true)
      setDismissible(false)
      setDisableCNP(true)
      // Temporary: Adding a delay before reprocessing a transaction
      await wait(REPROCESS_DELAY)

      const data = await reprocessTransaction({
        currency,
        registerId,
        registerSalePaymentId,
        paymentIntentId: paymentState.paymentIntentId,
        withSurcharge,
        ...(tipEligibleAmount ? { tipEligibleAmount } : {}),
      })
      if (data.status === LSPAY_TRANSACTION_STATUS.PROCESSING) {
        terminalMonitoringInstance?.sendEvent('START', {
          uniqueClientTxId: registerSalePaymentId,
          payment: { id: data.payment_intent_id },
          terminal: {
            id: pairing?.deviceID,
            model: deviceType,
          },
          transactionType: TRANSACTION_TYPE.PAYMENT,
        })
        dispatch({
          type: 'START_POLLING',
          paymentIntentId: data.payment_intent_id,
        })
        return
      }
      if (data.status === LSPAY_TRANSACTION_STATUS.DECLINED) {
        // remove the active lspay transaction from storage for declined transactions
        removeFromStorage(ACTIVE_LSPAY_TRANSACTION)

        dispatch({
          type: 'DECLINED',
          errorMessage: getErrorMessage(
            getPaymentDeclinedError(data.reason),
            isCardSpecificDecline(data.reason)
          ),
          paymentIntentId: data.payment_intent_id,
        })
        return
      }
      // Ideally, reprocess request should not lead to a completed/authorised/cancelled transaction
      // However, the Stripe false negative error can lead to an out of sync between Stripe and Swiper
      // Swiper could have a chance to re-sync with Stripe and get the correct status when handling reprocess API
      if (isTransactionCompleted(data)) {
        dispatch({
          type: 'POLL_RESULT_OK',
          transaction: data.transaction,
        })
        return
      }
      if (data.status === LSPAY_TRANSACTION_STATUS.AUTHORISED) {
        dispatch({
          type: 'AUTHORISED',
          paymentIntentId: data.payment_intent_id,
        })
        return
      }
      if (data.status === LSPAY_TRANSACTION_STATUS.CANCELLED) {
        dispatch({
          type: 'CANCELLED',
          errorMessage: getErrorMessage(LSPAY_ERROR.CANCEL_PAYMENT),
          paymentIntentId: data.payment_intent_id,
          reason: data.reason,
        })
        return
      }
    } catch (error: any) {
      // track the error
      trackLSPayServerError(
        '[lspay-server-driven] failed to reprocess transaction',
        error,
        {
          registerSalePaymentId,
          registerSaleId,
          paymentIntentId: paymentState.paymentIntentId,
        },
        TAG.payment
      )

      // Retry reprocess if it fails
      dispatch({
        type: 'DECLINED',
        errorMessage: getErrorMessage(LSPAY_ERROR.DEFAULT_PAYMENT),
        paymentIntentId: paymentState.paymentIntentId,
      })
      return
    } finally {
      setIsRetrying(false)
      setDismissible(true)
      setDisableCNP(false)
    }
  }

  useEffect(() => {
    terminalMonitoringInstance?.sendEvent('START', {
      uniqueClientTxId: registerSalePaymentId,
      terminal: {
        id: terminalInfo.deviceId,
        model: deviceType,
      },
      transactionType: TRANSACTION_TYPE.PAYMENT,
    })
    return () => {
      // Send an error event to the terminal monitoring instance when the component is unmounted
      // There is an exception - amount too small error would call setSwiperError to show a higher level
      // error modal (not in the server-driven component) which would bypass this and send the event by itself
      if (paymentStateRef.current.status === 'error') {
        const swiperError = paymentStateRef.current.swiperError
        sendTerminalEvent({
          terminalMonitoringInstance,
          terminal: {
            id: terminalInfo.deviceId,
            model: deviceType,
          },
          reason:
            swiperError.message ||
            `Error happened during ${paymentStateRef.current.previousState.status}`,
          swiperError,
          transactionType: TRANSACTION_TYPE.PAYMENT,
        })
      }
    }
  }, [
    deviceType,
    registerSalePaymentId,
    terminalInfo.deviceId,
    terminalMonitoringInstance,
  ])

  // setSwiperError shows the error message on the LSPaymentModal component that overrides the error message component
  // in the server-driven component, so we need to explicitly send the event to the terminal monitoring instance here
  const setSwiperErrorAndSendEvent = useCallback(
    (error: SwiperError) => {
      terminalMonitoringInstance?.sendEvent('COMPLETE', {
        payment: {
          status: 'ERROR',
          reason: error.message,
        },
        error: {
          code: error.code.toString(),
          message: error.message,
        },
        terminal: {
          id: terminalInfo.deviceId,
          model: deviceType,
        },
        transactionType: TRANSACTION_TYPE.PAYMENT,
      })
      setSwiperError(error)
    },
    [
      deviceType,
      setSwiperError,
      terminalInfo.deviceId,
      terminalMonitoringInstance,
    ]
  )

  const getPaymentContent = (): ReactNode => {
    switch (paymentState.status) {
      case 'creatingTransaction': {
        return (
          <CreatingTransaction
            amount={amount}
            currency={currency}
            retailerPaymentTypeId={retailerPaymentTypeId}
            registerId={registerId}
            registerSaleId={registerSaleId}
            registerSalePaymentId={registerSalePaymentId}
            dispatch={dispatch}
            withSurcharge={withSurcharge}
            tipEligibleAmount={tipEligibleAmount}
            terminalMonitoringInstance={terminalMonitoringInstance}
            setCancelProcessing={setCancelProcessing}
            setSwiperError={setSwiperErrorAndSendEvent}
            terminalInfo={terminalInfo}
          />
        )
      }
      case 'polling': {
        return (
          <PollTransaction
            paymentIntentId={paymentState.paymentIntentId}
            registerId={register.id}
            registerSalePaymentId={registerSalePaymentId}
            registerSaleId={registerSaleId}
            dispatch={dispatch}
            terminalMonitoringInstance={terminalMonitoringInstance}
            terminalInfo={terminalInfo}
          />
        )
      }
      case 'capturing': {
        return (
          <CaptureTransaction
            paymentIntentId={paymentState.paymentIntentId}
            registerId={register.id}
            registerSalePaymentId={registerSalePaymentId}
            dispatch={dispatch}
            setDisableCNP={setDisableCNP}
            terminalMonitoringInstance={terminalMonitoringInstance}
            terminalInfo={terminalInfo}
          />
        )
      }
      case 'done': {
        return null
      }
      case 'recovering': {
        return (
          <RecoverTransaction
            paymentIntentId={paymentState.paymentIntentId}
            registerId={register.id}
            registerSalePaymentId={registerSalePaymentId}
            registerSaleId={registerSaleId}
            dispatch={dispatch}
            setDisableCNP={setDisableCNP}
          />
        )
      }
      case 'cancelled':
      case 'declined':
      case 'error': {
        return (
          <ErrorContent
            headingType="sub-heading"
            lspayError={paymentState.errorMessage}
            retry={() => {
              handleRetry(paymentState.previousState, dispatch)
            }}
            close={onError}
            showWarningIcon={paymentState.status !== 'cancelled'}
            isRetrying={isRetrying}
            disableRetry={
              paymentState.status === 'error' && paymentState.disableRetry
            }
          />
        )
      }
      default: {
        // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
        const _exhaustiveCheck: never = paymentState
        return _exhaustiveCheck
      }
    }
  }

  return <>{getPaymentContent()}</>
}
export { LSPAYPaymentServerDriven }
